import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Zap, Globe, Shield, TrendingUp, Scale, ArrowLeft, Lightbulb } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const ICONS = [Zap, Globe, Scale, Shield, TrendingUp];

export default function WhyKwhPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [rates, setRates] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/exchange-rates/why-kwh`, { params: { lang: i18n.language } }),
      axios.get(`${API}/exchange-rates/`),
    ]).then(([whyRes, ratesRes]) => {
      setData(whyRes.data);
      setRates(ratesRes.data);
    }).catch(() => {});
  }, [i18n.language]);

  if (!data) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-amber-50/30">
      {/* Hero */}
      <section className="relative py-20 px-4 text-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-100/50 to-orange-100/30" />
        <div className="relative max-w-3xl mx-auto">
          <Button variant="ghost" onClick={() => navigate(-1)} className="absolute left-0 top-0" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5 mr-1" /> {t('common.back')}
          </Button>
          <Badge className="bg-amber-500 text-white px-4 py-1 mb-6">
            <Lightbulb className="w-4 h-4 mr-1" /> kWh Pricing
          </Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-amber-900 mb-4" data-testid="why-kwh-title">
            {data.title}
          </h1>
          <p className="text-base sm:text-lg text-amber-700">{data.subtitle}</p>
        </div>
      </section>

      {/* Points */}
      <section className="max-w-4xl mx-auto px-4 -mt-6 space-y-6 mb-16">
        {data.points.map((point, i) => {
          const Icon = ICONS[i % ICONS.length];
          return (
            <Card key={i} className="border-amber-100 hover:shadow-lg transition-shadow" data-testid={`why-point-${i}`}>
              <CardContent className="p-6 flex gap-5">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Icon className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-amber-900 mb-1">{point.title}</h3>
                  <p className="text-amber-700 text-sm leading-relaxed">{point.description}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </section>

      {/* Live Conversion Table */}
      <section className="max-w-4xl mx-auto px-4 mb-20">
        <h2 className="text-2xl font-bold text-amber-900 text-center mb-2">
          {t('common.dianUnit')} → {t('artwork.price')}
        </h2>
        <p className="text-center text-amber-600 mb-8 text-sm">
          1,000 kWh artwork example
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {rates.slice(0, 8).map(rate => (
            <Card key={rate.currency} className="border-amber-100 text-center">
              <CardContent className="p-4">
                <p className="text-xs text-amber-500 mb-1">{rate.country}</p>
                <p className="text-2xl font-bold text-amber-900">
                  {rate.symbol}{(1000 * rate.rate).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </p>
                <p className="text-xs text-amber-600">{rate.currency}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}
